import { apiClient } from './apiClient';

// Notification interfaces
export interface Notification {
  id: number;
  title: string;
  body: string;
  type: 'booking' | 'promotion' | 'system_update' | 'review' | 'payment' | 'general';
  data?: Record<string, any>;
  is_read: boolean;
  created_at: string;
  updated_at: string;
  user_id: number;
  channels?: ('push' | 'email' | 'sms')[];
}

export interface NotificationFilters {
  page?: number;
  limit?: number;
  unread_only?: boolean;
  type?: string;
}

export interface NotificationListResponse {
  data: Notification[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  unreadCount: number;
}

export interface NotificationSettings {
  push_enabled: boolean;
  email_enabled: boolean;
  sms_enabled: boolean;
  preferences: {
    booking_updates: boolean;
    promotions: boolean;
    reviews: boolean;
    system_updates: boolean;
  };
}

export interface SendNotificationRequest {
  users: string[];
  title: string;
  body: string;
  data?: Record<string, any>;
  channels: ('push' | 'email' | 'sms')[];
}

export interface BulkNotificationRequest {
  filter: {
    role?: string;
    status?: string;
    last_active_after?: string;
  };
  title: string;
  body: string;
  data?: Record<string, any>;
  channels: ('push' | 'email' | 'sms')[];
}

export interface NotificationStats {
  total_sent: number;
  total_delivered: number;
  total_read: number;
  total_clicked: number;
  by_type: Record<string, number>;
  by_channel: Record<string, number>;
  delivery_rate: number;
  read_rate: number;
  click_rate: number;
}

export interface NotificationUser {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
}

// User notification methods
export const notificationService = {
  // Get user notifications
  getUserNotifications: async (filters: NotificationFilters = {}): Promise<NotificationListResponse> => {
    console.log('🔔 Getting user notifications with filters:', filters);
    
    const params = new URLSearchParams();
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.unread_only !== undefined) params.append('unread_only', filters.unread_only.toString());
    if (filters.type) params.append('type', filters.type);

    const response = await apiClient.get(`/user/notifications?${params.toString()}`);
    console.log('🔔 User notifications response:', response.data);
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId: number): Promise<void> => {
    console.log('🔔 Marking notification as read:', notificationId);
    await apiClient.patch(`/user/notifications/${notificationId}/read`);
    console.log('🔔 Notification marked as read successfully');
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<void> => {
    console.log('🔔 Marking all notifications as read');
    await apiClient.patch('/user/notifications/read-all');
    console.log('🔔 All notifications marked as read successfully');
  },

  // Delete notification
  deleteNotification: async (notificationId: number): Promise<void> => {
    console.log('🔔 Deleting notification:', notificationId);
    await apiClient.delete(`/user/notifications/${notificationId}`);
    console.log('🔔 Notification deleted successfully');
  },

  // Update FCM token
  updateFCMToken: async (fcmToken: string): Promise<void> => {
    console.log('🔔 Updating FCM token');
    await apiClient.put('/user/notifications/token', { fcm_token: fcmToken });
    console.log('🔔 FCM token updated successfully');
  },

  // Get notification settings
  getNotificationSettings: async (): Promise<NotificationSettings> => {
    console.log('🔔 Getting notification settings');
    const response = await apiClient.get('/user/notifications/settings');
    console.log('🔔 Notification settings response:', response.data);
    return response.data;
  },

  // Update notification settings
  updateNotificationSettings: async (settings: NotificationSettings): Promise<void> => {
    console.log('🔔 Updating notification settings:', settings);
    await apiClient.put('/user/notifications/settings', settings);
    console.log('🔔 Notification settings updated successfully');
  },
};

// Admin notification methods
export const adminNotificationService = {
  // Send notification to specific users
  sendNotification: async (data: SendNotificationRequest): Promise<void> => {
    console.log('🔔 Admin sending notification:', data);
    
    // Validate and clean data
    const cleanData = {
      users: data.users.filter(id => id && id.trim() !== ''), // Remove empty user IDs
      title: data.title?.trim(),
      body: data.body?.trim(),
      data: data.data || {},
      channels: data.channels || ['push']
    };
    
    console.log('🔔 Cleaned notification data:', cleanData);
    
    if (cleanData.users.length === 0) {
      throw new Error('At least one user ID is required');
    }
    
    if (!cleanData.title || !cleanData.body) {
      throw new Error('Title and body are required');
    }
    
    try {
      await apiClient.post('/admin/notifications', cleanData);
      console.log('🔔 Notification sent successfully');
    } catch (error: any) {
      console.error('🔔 Failed to send notification:', error);
      console.error('🔔 Error details:', error.response?.data);
      throw error;
    }
  },

  // Send bulk notifications
  sendBulkNotification: async (data: BulkNotificationRequest): Promise<void> => {
    console.log('🔔 Admin sending bulk notification:', data);
    await apiClient.post('/admin/notifications/bulk', data);
    console.log('🔔 Bulk notification sent successfully');
  },

  // Get notification stats
  getNotificationStats: async (startDate?: string, endDate?: string, type?: string): Promise<NotificationStats> => {
    console.log('🔔 Getting notification stats');
    
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (type) params.append('type', type);

    const response = await apiClient.get(`/admin/notifications/stats?${params.toString()}`);
    console.log('🔔 Notification stats response:', response.data);
    return response.data;
  },

  // Get users for notification targeting
  getUsers: async (search?: string, limit: number = 50): Promise<NotificationUser[]> => {
    console.log('🔔 Getting users for notification targeting');
    
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('limit', limit.toString());

    try {
      const response = await apiClient.get(`/admin/users?${params.toString()}`);
      console.log('🔔 Users response:', response.data);
      
      // Transform the response to match NotificationUser interface
      const users = response.data?.data || response.data || [];
      return users.map((user: any) => ({
        id: user.id?.toString() || user.userId?.toString(),
        name: user.name || user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        email: user.email,
        role: user.role || user.roleId?.toString(),
        status: user.status || user.isActive ? 'active' : 'inactive'
      }));
    } catch (error: any) {
      console.error('🔔 Failed to get users:', error);
      // Return empty array as fallback
      return [];
    }
  },
};

// Calculate stats from notifications data (fallback)
export const calculateNotificationStats = (notifications: Notification[]): Partial<NotificationStats> => {
  const total = notifications.length;
  const readCount = notifications.filter(n => n.is_read).length;
  
  const byType = notifications.reduce((acc, notification) => {
    acc[notification.type] = (acc[notification.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return {
    total_sent: total,
    total_read: readCount,
    by_type: byType,
    read_rate: total > 0 ? (readCount / total) * 100 : 0,
  };
};
